import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { red } from '@mui/material/colors';
import { TimePicker } from '@mui/x-date-pickers';
import { format, parse } from 'date-fns';
import { Field, Form, Formik } from 'formik';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { createCameraSchedule, deleteCameraSchedule, patchCameraSchedule } from '@sophocles/api/odlApp';
import DayOfWeekSelectionComponent from '@sophocles/components/DayOfWeekSelectionComponent';
import FormErrorAlert from '@sophocles/components/FormErrorAlert';
import { addToast } from '@sophocles/features/toast';
import { applyApiErrorToFormik, parseApiError } from '@sophocles/utils/apiErrors';

// `days` isn't a Formik field (day checkboxes are component state, not part
// of Formik's initialValues), so only `time` can bind an inline error.
const KNOWN_FIELDS = ['time'];

// ── helpers ───────────────────────────────────────────────────────────────────

function convertDayOfWeekNumberToArray(days) {
  if (days == null) return [false, false, false, false, false, false, false];
  return [2, 4, 8, 16, 32, 64, 128].map(bit => !!(bit & days));
}

function convertDayOfWeekArrayToNumber(array) {
  if (array.length < 7) return null;
  let count = 0;
  for (let i = 0; i < 7; i++) {
    if (array[i]) count += 2 ** (i + 1);
  }
  return count;
}

// ── DeleteCameraScheduleDialog ────────────────────────────────────────────────

function DeleteCameraScheduleDialog({ siteId, camId, schedule, open, closeDialog }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);

  const handleDelete = () => {
    setIsDeleting(true);
    setErrorMessages([]);
    deleteCameraSchedule(siteId, camId, schedule.id ?? 0)
      .then(() => {
        dispatch(
          addToast({
            title: t('general.success', 'Success'),
            message: t('cameraSchedule.deleted', 'Camera schedule deleted'),
            level: 'info',
            dismissible: true,
          }),
        );
        closeDialog();
      })
      .catch(error => {
        setErrorMessages(
          parseApiError(error, {
            knownFields: [],
            genericMessage: t('cameraSchedule.deleteFailed', 'Failed to delete camera schedule'),
          }).nonFieldMessages,
        );
      })
      .finally(() => setIsDeleting(false));
  };

  const actions = [
    <Button key="cancel" variant="outlined" onClick={closeDialog} disabled={isDeleting}>
      {t('actions.cancel', 'Cancel')}
    </Button>,
    <Button key="yes" variant="contained" onClick={handleDelete} disabled={isDeleting}>
      {t('general.yes', 'Yes')}
    </Button>,
  ];

  return (
    <Dialog open={open} onClose={closeDialog}>
      <DialogTitle sx={{ backgroundColor: red[700], color: 'white' }}>
        {t('cameraSchedule.deleteScheduleTitle', 'Delete Schedule')}
      </DialogTitle>
      <DialogContent dividers>
        <div>{t('cameraSchedule.deleteConfirm', 'are you sure you want to delete this schedule?')}</div>
        <FormErrorAlert messages={errorMessages} sx={{ mt: 2 }} />
      </DialogContent>
      <DialogActions>{actions}</DialogActions>
    </Dialog>
  );
}

// ── ScheduleDialog ────────────────────────────────────────────────────────────

function ScheduleDialog({ siteId, camera, schedule, open, closeDialog }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [daysOfWeekArray, setDaysOfWeekArray] = useState(() =>
    convertDayOfWeekNumberToArray(schedule ? (schedule.days ?? null) : null),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nonFieldMessages, setNonFieldMessages] = useState([]);

  // Keep daysOfWeekArray in sync when the schedule prop changes (dialog reuse).
  const [prevSchedule, setPrevSchedule] = useState(schedule);
  if (schedule !== prevSchedule) {
    setPrevSchedule(schedule);
    setDaysOfWeekArray(convertDayOfWeekNumberToArray(schedule ? (schedule.days ?? null) : null));
  }

  const toggleDayOfWeek = index => {
    setDaysOfWeekArray(prev => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  const camId = camera.id ?? 0;

  let time = schedule ? (schedule.time ?? null) : null;
  if (time == null) time = '00:00:00';
  const cameraNum = camera.camera_number ?? 0;

  const canSubmit = !isSubmitting && daysOfWeekArray.some(v => v === true);

  const submit = (values, { setErrors }) => {
    const days = convertDayOfWeekArrayToNumber(daysOfWeekArray);
    const timeStr = format(values.time, 'HH:mm:ss');
    const finalPayload = { days, time: timeStr };

    setIsSubmitting(true);
    setNonFieldMessages([]);

    const writePromise =
      schedule != null
        ? patchCameraSchedule(siteId, camId, schedule.id ?? 0, finalPayload)
        : createCameraSchedule(siteId, camId, finalPayload);

    writePromise
      .then(() => {
        dispatch(
          addToast({
            title: t('general.success', 'Success'),
            message:
              schedule != null
                ? t('cameraSchedule.updated', 'Camera schedule updated')
                : t('cameraSchedule.created', 'Camera schedule created'),
            level: 'success',
            dismissible: true,
          }),
        );
        closeDialog();
      })
      .catch(error => {
        setNonFieldMessages(
          applyApiErrorToFormik(error, {
            setErrors,
            knownFields: KNOWN_FIELDS,
            genericMessage:
              schedule != null
                ? t('cameraSchedule.updateFailed', 'Failed to update camera schedule')
                : t('cameraSchedule.createFailed', 'Failed to create camera schedule'),
          }),
        );
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <Dialog open={open} onClose={closeDialog}>
      <DialogTitle>
        {schedule
          ? t('cameraSchedule.editingTitle', 'Editing Camera {{number}} Schedule', { number: cameraNum })
          : t('cameraSchedule.addingTitle', 'Adding Camera {{number}} Schedule', { number: cameraNum })}
      </DialogTitle>
      <DialogContent dividers>
        <Formik initialValues={{ time: parse(time, 'HH:mm:ss', new Date()) }} onSubmit={submit}>
          {({ values }) => (
            <Form id="camera-schedule-form">
              <FormErrorAlert messages={nonFieldMessages} sx={{ mb: 2 }} />
              <div style={{ padding: '10px', marginRight: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    marginBottom: '16px',
                  }}
                >
                  <Field name="time">
                    {({ field, form: formBag, meta }) => (
                      <TimePicker
                        value={field.value}
                        onChange={newTime => formBag.setFieldValue(field.name, newTime)}
                        slotProps={{
                          textField: {
                            label: t('cameraSchedule.imageCaptureTime', 'Image Capture Time'),
                            error: meta.touched && Boolean(meta.error),
                            helperText: meta.touched && meta.error ? meta.error : '',
                          },
                        }}
                      />
                    )}
                  </Field>
                </div>
                <div>
                  <DayOfWeekSelectionComponent selected={daysOfWeekArray} toggle={toggleDayOfWeek} />
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </DialogContent>
      <DialogActions>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row-reverse', width: '100%' }}>
          <div>
            <Button variant="outlined" onClick={closeDialog} disabled={isSubmitting}>
              {t('actions.cancel', 'Cancel')}
            </Button>
            <Button variant="contained" disabled={!canSubmit} type="submit" form="camera-schedule-form" sx={{ ml: 1 }}>
              {t('actions.submit', 'Submit')}
            </Button>
          </div>

          {schedule != null ? (
            <div>
              <Button color="error" onClick={() => setDeleteOpen(true)} disabled={isSubmitting}>
                {t('cameraSchedule.deleteScheduleButton', 'Delete Schedule')}
              </Button>
              <DeleteCameraScheduleDialog
                open={deleteOpen}
                closeDialog={() => setDeleteOpen(false)}
                siteId={siteId}
                camId={camId}
                schedule={schedule}
              />
            </div>
          ) : null}
        </div>
      </DialogActions>
    </Dialog>
  );
}

export default ScheduleDialog;
